<template>
  <div class="static-page">
    <HeadNav selectedLanguage="en" :page="pageKey" />

    <main class="container">
      <article class="content">
        <p class="eyebrow">Secure VPN</p>
        <h1>{{ page.title }}</h1>
        <section v-for="(section, idx) in page.sections" :key="idx" class="content-section">
          <h2 v-if="section.heading">{{ section.heading }}</h2>
          <p v-for="(paragraph, pIdx) in section.paragraphs" :key="pIdx">
            <template v-if="section.mailto && paragraph.includes(section.mailto)">
              {{ paragraph.split(section.mailto)[0] }}
              <a class="mail-link" :href="`mailto:${section.mailto}`">{{ section.mailto }}</a>
              {{ paragraph.split(section.mailto)[1] }}
            </template>
            <template v-else>{{ paragraph }}</template>
          </p>
          <ul v-if="section.items">
            <li v-for="item in section.items" :key="item">{{ item }}</li>
          </ul>
        </section>
      </article>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

const pages = {
  about: {
    title: 'About Secure VPN',
    sections: [
      {
        paragraphs: [
          'Secure VPN is dedicated to providing users with reliable information about VPN technology, online privacy, and secure internet access. Our goal is to help users better understand VPN features and make informed decisions when choosing a secure connection solution.',
          'With the increasing need for online privacy and safer browsing, VPN services have become an important tool for protecting internet connections. Secure VPN focuses on sharing useful guides, feature explanations, and helpful resources related to VPN usage.',
          'Our website provides information about Secure VPN features, supported devices, connection methods, security benefits, and common VPN questions. We aim to make VPN technology easier to understand for everyday users.',
          'Whether you want to protect your connection on public Wi-Fi, improve online privacy, or learn how VPN applications work, Secure VPN provides simple and practical information to help you get started.',
        ],
      },
      {
        heading: 'Our Mission',
        paragraphs: [
          'Our mission is to make online security easier and more accessible. We believe users should have clear information about privacy tools and understand how VPN technology can help create a safer online experience.',
        ],
      },
      {
        heading: 'What We Provide',
        paragraphs: ['Secure VPN provides:'],
        items: ['VPN guides and tutorials', 'Download information', 'Feature explanations', 'Privacy and security tips', 'Answers to common VPN questions'],
      },
      {
        paragraphs: ['We continuously improve our content to provide accurate, useful, and user-friendly information.'],
      },
    ],
  },
  contact: {
    title: 'Contact Us',
    sections: [
      {
        paragraphs: [
          'We value your feedback and questions. If you have any inquiries about Secure VPN, website content, or general VPN information, feel free to contact us.',
          'Our team reviews user feedback and suggestions to improve the quality of our resources and provide a better experience for visitors.',
        ],
      },
      {
        heading: 'Get in Touch',
        paragraphs: [
          'For questions, suggestions, or feedback, please contact us through:',
          'Email: support@secureproxyvpn.com',
        ],
        mailto: 'support@secureproxyvpn.com',
      },
      {
        heading: 'Contact Us For',
        paragraphs: ['You can contact us regarding:'],
        items: ['Website feedback', 'Content suggestions', 'Technical questions', 'Copyright concerns', 'General inquiries'],
      },
      {
        paragraphs: ['We aim to respond to legitimate requests as quickly as possible.'],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    sections: [
      {
        paragraphs: [
          'At Secure VPN, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard information when you visit our website.',
        ],
      },
      {
        heading: 'Information We Collect',
        paragraphs: ['We may collect limited information automatically when you visit our website, including:'],
        items: ['Browser type', 'Device information', 'IP address', 'Pages visited', 'Website usage data'],
      },
      {
        paragraphs: ['This information helps us improve website performance and user experience.'],
      },
      {
        heading: 'Cookies',
        paragraphs: [
          'Our website may use cookies to improve functionality and analyze visitor behavior. Cookies help us understand how users interact with our website and allow us to provide better content.',
          'You can manage or disable cookies through your browser settings.',
        ],
      },
      {
        heading: 'Third-Party Services',
        paragraphs: ['We may use third-party services such as analytics providers or advertising partners. These services may collect information according to their own privacy policies.'],
      },
      {
        heading: 'Data Security',
        paragraphs: ['We take reasonable steps to protect collected information and maintain a secure browsing environment. However, no online service can guarantee complete security.'],
      },
      {
        heading: 'Policy Updates',
        paragraphs: [
          'We may update this Privacy Policy from time to time. Any changes will be published on this page.',
          'Secure VPN services may not be available in certain regions, including mainland China. Users are responsible for checking local regulations and service availability before use.',
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of Use',
    sections: [
      {
        paragraphs: ['Welcome to Secure VPN. By accessing and using this website, you agree to follow these Terms of Use.'],
      },
      {
        heading: 'Website Information',
        paragraphs: ['The content provided on Secure VPN is for informational purposes only. We provide VPN-related guides, explanations, and resources to help users understand internet privacy and security.'],
      },
      {
        heading: 'User Responsibilities',
        paragraphs: [
          'Users are responsible for ensuring that their online activities comply with applicable laws and regulations in their location.',
          'You agree not to:',
        ],
        items: ['Misuse website content', 'Attempt unauthorized access', 'Distribute harmful materials', 'Use the website for illegal activities'],
      },
      {
        paragraphs: ['Secure VPN services may not be available in certain regions, including mainland China. Users are responsible for checking local regulations and service availability before use.'],
      },
      {
        heading: 'Content Accuracy',
        paragraphs: ['We make reasonable efforts to provide accurate and updated information. However, we cannot guarantee that all information is always complete or error-free.'],
      },
      {
        heading: 'Third-Party Links',
        paragraphs: ['Our website may contain links to third-party websites. We are not responsible for the content, policies, or services provided by external websites.'],
      },
      {
        heading: 'Changes to Terms',
        paragraphs: ['We may update these Terms of Use at any time. Continued use of the website means you accept the updated terms.'],
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    sections: [
      {
        paragraphs: ['The information provided on Secure VPN is intended for general informational purposes only. While we try to provide accurate and updated content, we do not guarantee that all information is complete, reliable, or suitable for every user.'],
      },
      {
        heading: 'VPN Usage',
        paragraphs: [
          'VPN services are designed to improve online privacy and security. Users are responsible for using VPN services according to local laws and regulations.',
          'Secure VPN does not encourage or support any illegal activities conducted through VPN services.',
        ],
      },
      {
        heading: 'Third-Party Services',
        paragraphs: ['This website may reference or link to third-party products, applications, or services. We are not responsible for the performance, security, or policies of external services.'],
      },
      {
        heading: 'No Professional Advice',
        paragraphs: ['The content on this website should not be considered professional legal, technical, or security advice. Users should make decisions based on their own requirements.'],
      },
      {
        heading: 'Contact',
        paragraphs: ['If you have questions regarding this Disclaimer, please contact us through our Contact page.'],
      },
    ],
  },
};

export default {
  name: 'StaticPage',
  components: {
    HeadNav,
  },
  computed: {
    pageKey() {
      return this.$route.meta.pageKey || 'about';
    },
    page() {
      return pages[this.pageKey] || pages.about;
    },
  },
};
</script>

<style lang="scss" scoped>
.static-page {
  min-height: 70vh;
  background:
    radial-gradient(ellipse 70% 40% at 10% 0%, rgba(91, 79, 232, 0.1), transparent 50%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
  padding: 56px 0 80px;
}

.content {
  background: #fff;
  border: 1px solid var(--vpn-line);
  border-radius: var(--vpn-radius);
  padding: 44px;
  box-shadow: var(--vpn-shadow-soft);
}

.eyebrow {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--vpn-teal);
}

h1 {
  margin: 0 0 28px;
  font-size: clamp(30px, 4vw, 42px);
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-weight: 800;
}

h2 {
  margin: 32px 0 12px;
  font-size: 24px;
  font-weight: 700;
  color: var(--vpn-ink);
}

p,
li {
  font-size: 16px;
  line-height: 1.8;
  color: var(--vpn-ink-soft);
}

p {
  margin: 0 0 14px;
}

ul {
  margin: 0 0 14px;
  padding-left: 22px;
}

.mail-link {
  color: var(--vpn-teal-deep);
  font-weight: 700;
  text-decoration: none;
  border-bottom: 1px solid rgba(67, 56, 202, 0.35);
}

.mail-link:hover {
  color: var(--vpn-teal);
}

@media (max-width: 720px) {
  .content {
    padding: 28px 20px;
  }
}
</style>
